import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail } from "lucide-react";
import { NSLogo, CONTACT } from "./NSLogo";
import ContactBar from "./ContactBar";
import { TID } from "@/lib/testIds";

const links = [
  { to: "/services", label: "Services", tid: TID.nav.services },
  { to: "/cities", label: "Cities", tid: TID.nav.cities },
  { to: "/coverage", label: "Coverage", tid: "nav-coverage" },
  { to: "/about", label: "About", tid: TID.nav.about },
  { to: "/journal", label: "Journal", tid: TID.nav.journal },
  { to: "/contact", label: "Contact", tid: "nav-contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid={TID.nav.root}
      className="fixed top-0 left-0 right-0 z-50"
    >
      <ContactBar />
      <div
        className={`transition-[background,border,padding] duration-500 ${
          scrolled
            ? "bg-[#F5F3EF]/90 backdrop-blur-md border-b border-[#D9D6D0] py-3"
            : "bg-[#F5F3EF]/70 backdrop-blur-sm border-b border-transparent py-5"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 flex items-center justify-between">
          <NSLogo testid={TID.nav.logo} />

          <nav className="hidden md:flex items-center gap-8 lg:gap-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.tid}
                className={({ isActive }) =>
                  `font-mono text-[11px] uppercase tracking-[0.24em] transition-colors duration-300 ${
                    isActive ? "text-[#C17A43]" : "text-[#111111] hover:text-[#C17A43]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={CONTACT.phoneHref}
              data-testid="header-phone-cta"
              className="hidden md:inline-flex items-center gap-2 border border-[#111111] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.24em] text-[#111111] hover:bg-[#111111] hover:text-[#F5F3EF] transition-colors duration-300"
            >
              <Phone size={12} strokeWidth={2} />
              <span>{CONTACT.phone}</span>
            </a>

            <button
              data-testid={TID.nav.mobileToggle}
              onClick={() => setOpen((s) => !s)}
              className="md:hidden text-[#111111] p-2"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="md:hidden bg-[#F5F3EF] border-t border-[#D9D6D0]"
            >
              <div className="px-6 py-6 flex flex-col gap-4">
                {links.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`${l.tid}-mobile`}
                    className="font-serif text-2xl text-[#111111] hover:italic hover:text-[#C17A43] transition-colors"
                  >
                    {l.label}
                  </Link>
                ))}

                <div className="mt-6 pt-6 border-t border-[#D9D6D0] flex flex-col gap-3">
                  <a
                    href={CONTACT.phoneHref}
                    data-testid="mobile-menu-phone"
                    className="inline-flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.22em] text-[#111111] hover:text-[#C17A43]"
                  >
                    <Phone size={14} strokeWidth={2} />
                    {CONTACT.phone}
                  </a>
                  <a
                    href={CONTACT.emailHref}
                    data-testid="mobile-menu-email"
                    className="inline-flex items-center gap-3 font-mono text-[12px] normal-case tracking-[0.08em] text-[#111111] hover:text-[#C17A43]"
                  >
                    <Mail size={14} strokeWidth={2} />
                    {CONTACT.email}
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
